import React, { useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { ChevronDown } from 'lucide-react';

interface CityData {
  name: string;
  value: number;
  amount: string;
  percentage: string;
  color: string;
}

const cityData: CityData[] = [
  { name: 'Mumbai', value: 345678, amount: '₹3,45,678', percentage: '(27.7%)', color: '#C1272D' },
  { name: 'Delhi', value: 212345, amount: '₹2,12,345', percentage: '(17.0%)', color: '#2563EB' },
  { name: 'Pune', value: 198765, amount: '₹1,98,765', percentage: '(15.9%)', color: '#16A34A' },
  { name: 'Nagpur', value: 145890, amount: '₹1,45,890', percentage: '(11.7%)', color: '#EA580C' },
  { name: 'Ahmedabad', value: 123456, amount: '₹1,23,456', percentage: '(9.9%)', color: '#9333EA' },
  { name: 'Others', value: 219544, amount: '₹2,19,544', percentage: '(17.8%)', color: '#9CA3AF' },
];

export const SalesByCityChart: React.FC = () => {
  const [period, setPeriod] = useState<'This Month' | 'Last Month' | 'This Quarter'>('This Month');

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#ECECF2] shadow-sm flex flex-col justify-between h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-800">Sales by City</h3>

        {/* Dropdown Filter */}
        <div className="relative group">
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            {period}
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-100 rounded-lg shadow-lg hidden group-hover:block z-10">
            {(['This Month', 'Last Month', 'This Quarter'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content (Flex Donut & Legend side by side) */}
      <div className="flex items-center justify-between gap-4 h-64">
        {/* Donut Chart */}
        <div className="w-1/2 h-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={cityData}
                cx="50%"
                cy="50%"
                innerRadius="55%"
                outerRadius="85%"
                paddingAngle={2}
                dataKey="value"
              >
                {cityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1E293B',
                  borderRadius: '10px',
                  border: 'none',
                  color: '#FFFFFF',
                  fontSize: '11px',
                }}
                formatter={(value: any) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Sales']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Breakdown Legend Table */}
        <div className="w-1/2 flex flex-col gap-2 justify-center pr-2">
          {cityData.map((city) => (
            <div key={city.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: city.color }}
                />
                <span className="font-semibold text-slate-700">{city.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800">{city.amount}</span>
                <span className="text-[11px] text-slate-400 font-medium w-11 text-right">
                  {city.percentage}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
